import { cn } from "~/ui-rtk/utils/tailwind-utils"
import DropdownMenu from "../DropdownMenu/DropdownMenu"
import { createElement, useEffect, useMemo, useState } from "react"
import { TableVisualizationColumn } from "~/ui-rtk/constants/table-visualization-types"
import { TSortingState } from "~/ui-rtk/hooks/url-sorting"
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline"
import Button from "../Button"

export default function SortByControl({
  align = "left",
  columnConfig,
  sortBy,
  onSort,
  className = "",
}: {
  align?: "left" | "center" | "right"
  columnConfig: TableVisualizationColumn[]
  sortBy?: TSortingState
  onSort: (sort: TSortingState) => void
  className?: string
}) {
  const [sortByKey, setSortByKey] = useState<string | undefined>(sortBy?.id)
  const [sortByDesc, setSortByDesc] = useState<boolean | undefined>(
    sortBy?.desc,
  )

  // Keep local state in sync when the sort changes externally (e.g. restored
  // from the URL or set by a preset). Depend on the primitive values, not the
  // sortBy object, so a referentially-new-but-equal prop doesn't loop.
  useEffect(() => {
    setSortByKey(sortBy?.id)
    setSortByDesc(sortBy?.desc)
  }, [sortBy?.id, sortBy?.desc])

  const sortFieldLabel = useMemo(() => {
    if (!sortByKey) {
      return "-"
    }
    return columnConfig.find(({ key }) => key === sortByKey)?.header ?? "-"
  }, [sortByKey, columnConfig])

  useEffect(() => {
    if (typeof sortByDesc !== "undefined" && sortByKey) {
      onSort({
        id: sortByKey,
        desc: sortByDesc,
      })
    }
  }, [sortByDesc, sortByKey])

  const handleSortClick = () => {
    setSortByDesc(!sortByDesc)
  }

  const ascDescButton = useMemo(() => {
    let component:
      | typeof ChevronUpDownIcon
      | typeof ArrowUpIcon
      | typeof ArrowDownIcon = ArrowDownIcon
    if (typeof sortByDesc !== "undefined") {
      if (sortByDesc) {
        component = ArrowDownIcon
      } else {
        component = ArrowUpIcon
      }
    }

    return createElement(component, {
      width: 10,
    })
  }, [sortByDesc])

  const dropdownOptions = useMemo(
    () =>
      columnConfig
        .filter(column => !column.disableSorting)
        .map(column => ({
          label: column.header ?? column.key,
          value: column.key,
        })),
    [columnConfig],
  )

  return (
    <div
      className={cn(
        "join border border-basic-blue bg-basic-dark-blue rounded-md items-center cursor-pointer bg-system-blue-700 border border-system-blue-600 text-base rounded-system-md",
        {
          "justify-start": align === "left",
          "justify-center": align === "center",
          "justify-end": align === "right",
        },
        className,
      )}
    >
      <DropdownMenu
        options={dropdownOptions}
        onItemClick={value => setSortByKey(String(value))}
        containerClassName="flex items-center"
        className="btn selection:join-item btn-sm bg-system-blue-700 hover:bg-system-blue-700"
      >
        <div>Sort By: {sortFieldLabel}</div>
      </DropdownMenu>
      <Button
        variant={{
          variant: "text",
        }}
        onClick={() => handleSortClick()}
        size="sm"
        className="px-2"
      >
        {ascDescButton}
      </Button>
    </div>
  )
}
